// src/features/collaboration/services/sharingService.js
import { doc, updateDoc, increment, arrayUnion } from "firebase/firestore";
import { db } from "../../../firebase/config";


export const generateShareData = (post, baseUrl = window.location.origin) => {
  if (!post) return null;
  
  const postUrl = `${baseUrl}/post/${post.id}`;
  const authorName = post.author?.name || 'an educator';
  let postText = post.content || '';
  
  // Trim content if too long
  if (postText.length > 100) {
    postText = postText.substring(0, 97) + '...';
  }
  
  // Create base share data
  const shareData = {
    url: postUrl,
    title: `Shared post from ${authorName} on EduPlatform`,
    text: `"${postText}" - shared from ${authorName} on EduPlatform`,
    hashtags: ['education', 'teaching', 'eduplatform']
  };
  
  // Add image if available
  if (post.image) {
    shareData.imageUrl = post.image;
  } else if (post.mediaType === 'image' && post.mediaUrl) {
    shareData.imageUrl = post.mediaUrl;
  }
  
  return shareData;
};

export const sharePost = async (postId, userId, platform) => {
  try {
    // Get a reference to the post
    const postRef = doc(db, "posts", postId);
    
    // Update the post to increment share count and record share action
    await updateDoc(postRef, {
      shareCount: increment(1),
      sharedBy: arrayUnion({
        userId,
        platform,
        timestamp: new Date().toISOString() // Use ISO string timestamp instead of serverTimestamp
      })
    });
    
    // Return success result
    return { 
      success: true, 
      platform,
      postId
    };
  } catch (error) {
    console.error("Error sharing post:", error);
    throw error;
  }
};

export const shareViaNavigator = async (shareData) => {
  if (navigator.share && shareData) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (error) {
      console.error("Error sharing via navigator:", error);
      return false;
    }
  }
  return false;
};

export const generateSharingUrl = (platform, data) => {
  const { url, title, text, hashtags = [], imageUrl } = data;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text);
  const encodedHashtags = hashtags.join(',');
  
  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}&hashtags=${encodedHashtags}`;
    
    case 'facebook':
      // Facebook sharing with image (requires Open Graph tags on the target page)
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    
    case 'linkedin':
      // LinkedIn sharing - support for image relies on Open Graph tags
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    
    case 'email':
      // Email can include text but not direct images
      return `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`;
    
    case 'whatsapp':
      // WhatsApp sharing with text and URL
      return `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`;
    
    case 'telegram':
      // Telegram sharing with text and URL
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
    
    default:
      return url;
  }
};